import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BlocksIcon, BookMarkedIcon, BotIcon, BoxIcon, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { SavedFilterView } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  FilterDefinition,
  SavedFilterItem,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { DEFAULT_ACTIVITY_LOG_SELECTION, useActivityLogs } from '@/hooks/useActivityLogs';
import { getFilterValues, migrateFilters, useTableFilters } from '@/hooks/useTableFilters';
import {
  type DateSelection,
  parseDateSelection,
  resolveDateSelection,
} from '@/modules/analytics/dateSelection';
import { selectAccountSlug } from '@/redux/session/selectors';
import { urlSafeBase64Decode } from '@/util/util';

import { useActivityLogFilterOptions } from './useActivityLogFilterOptions';
import {
  ACTIVITY_LOG_FILTERS_PREFERENCES_KEY,
  ACTIVITY_LOG_FILTERS_STORAGE_KEY,
  FILTER_KEY,
  MAX_FILTER_VALUES,
} from '../constants';
import { deriveAuditParams } from '../deriveAuditParams';

/**
 * Orchestrating hook for the Activity Log page — modelled on `useCampaignLogsPage`.
 * Owns the chip filter state (`useTableFilters`), the date range, derives the backend
 * `*_ids[]` params, and runs the audit-log query.
 */
export const useActivityLogsPage = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const [openPillId, setOpenPillId] = useState<string | null>(null);

  const { optionsByFilter, loadedByFilter } = useActivityLogFilterOptions();
  // Date selection kept in local state (default: last 7 days), not persisted — the default
  // stays fresh each session, matching the behaviour of the former Redux slice.
  const [dateSelection, setDateSelection] = useState<DateSelection>(DEFAULT_ACTIVITY_LOG_SELECTION);
  const { startDate, endDate } = useMemo(
    () => resolveDateSelection(dateSelection),
    [dateSelection]
  );

  const {
    filters,
    activeFilterCount,
    setFilter: setFilterRaw,
    removeFilter,
    resetFilters,
    savedViews,
    activeView,
    hasUnsavedChanges,
    applyView,
    clearActiveView,
    saveView,
    renameView,
    updateView,
    deleteView,
  } = useTableFilters({
    storageKey: ACTIVITY_LOG_FILTERS_STORAGE_KEY,
    preferencesKey: ACTIVITY_LOG_FILTERS_PREFERENCES_KEY,
  });

  // Knowledge bases + Connections share the backend `target_ids[]` param (and its 25-cap),
  // so their combined selection — not just each filter — must stay within the limit.
  const setFilter = useCallback(
    (key: string, values: string[], exclude?: boolean) => {
      if (values.length > MAX_FILTER_VALUES) {
        toast.error(t('filters.max_values', { 0: MAX_FILTER_VALUES }));
        return;
      }
      if (key === FILTER_KEY.collections || key === FILTER_KEY.connections) {
        const otherKey =
          key === FILTER_KEY.collections ? FILTER_KEY.connections : FILTER_KEY.collections;
        const combined = values.length + getFilterValues(filters, otherKey).length;
        if (combined > MAX_FILTER_VALUES) {
          toast.error(t('filters.max_values', { 0: MAX_FILTER_VALUES }));
          return;
        }
      }
      setFilterRaw(key, values, exclude);
    },
    [filters, setFilterRaw, t]
  );

  // Hydrate filters + date from the URL once on mount (e.g. the Home "View all" deep links).
  // Read-only: we seed local state from the URL, we never write back to it.
  const [searchParams] = useSearchParams();
  const hydratedFromUrl = useRef(false);
  useEffect(() => {
    if (hydratedFromUrl.current) {
      return;
    }
    hydratedFromUrl.current = true;

    const dateParam = searchParams.get('date');
    if (dateParam) {
      try {
        const parsed = JSON.parse(urlSafeBase64Decode(dateParam));
        const urlSelection = parseDateSelection(parsed.selection);
        if (urlSelection) {
          setDateSelection(urlSelection);
        } else if (parsed.startDate && parsed.endDate) {
          setDateSelection({
            type: 'custom',
            startDate: parsed.startDate,
            endDate: parsed.endDate,
          });
        }
      } catch {
        // Ignore a malformed date param — fall back to the default range.
      }
    }

    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        const urlFilters = migrateFilters(JSON.parse(urlSafeBase64Decode(filtersParam)));
        if (urlFilters.length) {
          resetFilters();
          urlFilters.forEach((f) => setFilterRaw(f.key, f.values, f.exclude));
        }
      } catch {
        // Ignore malformed filters — keep the stored/default filters.
      }
    }
  }, [searchParams, setFilterRaw, resetFilters]);

  const filterDefinitions = useMemo(
    (): FilterDefinition[] => [
      {
        id: FILTER_KEY.people,
        label: t('activityLogs.filters.people'),
        icon: UsersIcon,
        type: 'checkbox',
        options: optionsByFilter[FILTER_KEY.people],
      },
      {
        id: FILTER_KEY.brains,
        label: t('activityLogs.filters.ai_agents'),
        icon: BotIcon,
        type: 'checkbox',
        options: optionsByFilter[FILTER_KEY.brains],
      },
      {
        id: FILTER_KEY.collections,
        label: t('activityLogs.filters.knowledge_bases'),
        icon: BookMarkedIcon,
        type: 'checkbox',
        options: optionsByFilter[FILTER_KEY.collections],
      },
      {
        id: FILTER_KEY.desks,
        label: t('activityLogs.filters.environments'),
        icon: BoxIcon,
        type: 'checkbox',
        options: optionsByFilter[FILTER_KEY.desks],
      },
      {
        id: FILTER_KEY.connections,
        label: t('activityLogs.filters.connections'),
        icon: BlocksIcon,
        type: 'checkbox',
        options: optionsByFilter[FILTER_KEY.connections],
      },
    ],
    [t, optionsByFilter]
  );

  const filterBadges = useMemo(
    () => buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter }),
    [filterDefinitions, filters, setFilter, removeFilter]
  );

  const auditParams = useMemo(
    () => deriveAuditParams(filters, optionsByFilter, loadedByFilter),
    [filters, optionsByFilter, loadedByFilter]
  );

  const { data, fetchNextPage, hasNextPage } = useActivityLogs(slug, {
    ...auditParams,
    startDate,
    endDate,
  });

  // --- Saved views ---

  const savedFilterItems: SavedFilterItem[] = useMemo(
    () => savedViews.map((v) => ({ id: v.id, name: v.name })),
    [savedViews]
  );

  const applyViewById = useCallback(
    (viewId: string) => {
      const view = savedViews.find((v) => v.id === viewId);
      if (view) {
        applyView(view);
      }
    },
    [savedViews, applyView]
  );

  const activeSavedFilterView: SavedFilterView | null = useMemo(
    () => (activeView ? { id: activeView.id, name: activeView.name } : null),
    [activeView]
  );

  const savedFiltersConfig = useMemo(
    () => ({
      savedFilters: savedFilterItems,
      activeSavedFilter: activeSavedFilterView,
      hasUnsavedChanges,
      onSave: saveView,
      onUpdate: updateView,
      onDelete: deleteView,
      onRename: renameView,
      onSelectView: applyViewById,
    }),
    [
      savedFilterItems,
      activeSavedFilterView,
      hasUnsavedChanges,
      saveView,
      updateView,
      deleteView,
      renameView,
      applyViewById,
    ]
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
    clearActiveView();
  }, [resetFilters, clearActiveView]);

  return {
    // Filters
    filters,
    activeFilterCount,
    setFilter,
    removeFilter,
    filterDefinitions,
    filterBadges,
    // Saved views
    savedFilterItems,
    savedFiltersConfig,
    applyViewById,
    handleClearFilters,
    openPillId,
    setOpenPillId,
    // Date
    startDate,
    endDate,
    dateSelection,
    setDateSelection,
    // Data
    data,
    fetchNextPage,
    hasNextPage,
  };
};

export type UseActivityLogsPageReturn = ReturnType<typeof useActivityLogsPage>;
